import { z } from "zod";

import { WidgetAppearanceSchema } from "./appearance";
import { parseWithCustomerConfigMessage } from "./parse-customer-config";
import { WalletInfoModeFieldsObjectSchema } from "./wallet-feature-fields";

// Tokenization widget only supports internal wallet mode — no external wallet
// integration exists. Override the shared schema to restrict the field.
const TokenizationWalletFieldsSchema = WalletInfoModeFieldsObjectSchema.extend({
  walletMode: z.literal("internal").default("internal"),
});

// Tokenization is designed as a desktop-first experience, so both width and height
// default to "desktop". This intentionally diverges from the base WidgetAppearanceSchema
// default of "mobile".
const TOKENIZATION_DEFAULT_APPEARANCE = {
  sizingMode: "fixed" as const,
  width: "desktop" as const,
  height: "desktop" as const,
  theme: "light" as const,
  builtByBlockdaemon: true,
  coreNavigation: true,
  externalLinks: true,
  locale: "en-US" as const,
  currency: "USD" as const,
} satisfies z.infer<typeof WidgetAppearanceSchema>;

const TokenizationFeaturesCoreSchema = z.object({
  tokenization: z
    .object({
      enabled: z.boolean(),
    })
    .strict(),
});

export const TokenizationFeaturesInputSchema = TokenizationFeaturesCoreSchema.extend(
  TokenizationWalletFieldsSchema.shape,
);

export type TokenizationPreviewFeatures = z.infer<typeof TokenizationFeaturesInputSchema>;

const TokenizationWidgetConfigBaseSchema = z.object({
  widgetType: z.literal("tokenization").optional().default("tokenization"),
  features: TokenizationFeaturesInputSchema,
  appearance: WidgetAppearanceSchema.default(TOKENIZATION_DEFAULT_APPEARANCE),
});

/**
 * Input shape for JSON Schema generation (`z.toJSONSchema`); no transforms.
 * The tokenization schema has no coercion or transforms, so the input and
 * validated shapes are identical — a single schema serves both purposes.
 */
export const TokenizationWidgetConfigInputSchema = TokenizationWidgetConfigBaseSchema;

export type TokenizationWidgetConfig = z.infer<typeof TokenizationWidgetConfigBaseSchema>;

export function validateTokenizationCustomerConfig(config: unknown): TokenizationWidgetConfig {
  return parseWithCustomerConfigMessage(
    TokenizationWidgetConfigBaseSchema,
    config,
    "Invalid tokenization customer config",
  );
}
